import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Tabs,
  Button,
  Typography,
  Row,
  Col,
  Avatar,
  Space,
  Divider,
  Descriptions,
  Spin,
  Alert,
  Form,
  Input,
  Modal,
  DatePicker,
  Table,
  Select,
  Checkbox,
  Upload
} from "antd";
import {
  UserOutlined,
  ArrowLeftOutlined,
  WalletOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SolutionOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  BellOutlined,
  UnlockOutlined,
  LockOutlined,
  FileProtectOutlined,
  PlusOutlined,
  IdcardOutlined,
  FileSearchOutlined,
  UploadOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  GiftOutlined
} from "@ant-design/icons";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Layout from "../../components/Layout";
import { hideLoader, showLoader } from "../../utils/loader";
import { customBaseUrl, httpGet, httpPut, httpPost, httpGetPdf } from "../../store/http";
import Swal from "sweetalert2";
import { NotificationManager } from "react-notifications";

// Importing shared sub-components. components
import { Transactions } from "./shared/components/transactions";
import { CustomerAccounts } from "./shared/components/accounts-wallets";
import { CustomerActivities } from "./shared/components/activities";
import { CustomerRewards } from "./shared/components/rewards";
import { ActivityLogs } from "./shared/components/activity-logs";
import AdminBillsPayment from "../all-users/AdminBillsPayment";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [form] = Form.useForm();
  const [ninForm] = Form.useForm();
  const [utilityForm] = Form.useForm();
  const [activationForm] = Form.useForm();

  // State Features
  const [isStatementModalVisible, setIsStatementModalVisible] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [isBillsPaymentModalVisible, setIsBillsPaymentModalVisible] = useState(false);

  const [kycData, setKycData] = useState({ nin: null, cac: null, utility: null });
  const [kycLoading, setKycLoading] = useState(false);

  // Upload States
  const [isSubmitNinModalVisible, setIsSubmitNinModalVisible] = useState(false);
  const [isSubmitUtilityBillModalVisible, setIsSubmitUtilityBillModalVisible] = useState(false);
  const [submitNinLoading, setSubmitNinLoading] = useState(false);
  const [submitUtilityBillLoading, setSubmitUtilityBillLoading] = useState(false);
  const [selectedNinFile, setSelectedNinFile] = useState(null);
  const [selectedUtilityFile, setSelectedUtilityFile] = useState(null);
  const [uploadedNinUrl, setUploadedNinUrl] = useState(null);
  const [uploadedUtilityUrl, setUploadedUtilityUrl] = useState(null);

  // Bills History State
  const [billsPayment, setBillsPayment] = useState([]);
  const [billsPaymentLoading, setBillsPaymentLoading] = useState(false);
  const [billsPaymentPagination, setBillsPaymentPagination] = useState({ current: 0, pageSize: 10, total: 0 });

  // Activation Modal State
  const [isActivationModalVisible, setIsActivationModalVisible] = useState(false);
  const [pendingActivationState, setPendingActivationState] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);

  // Referee State
  const [referees, setReferees] = useState([]);
  const [refereesLoading, setRefereesLoading] = useState(false);
  const [refereePagination, setRefereePagination] = useState({ current: 0, pageSize: 10, total: 0 });

  // KYC Details Modal Visibility
  const [isNinDetailsVisible, setIsNinDetailsVisible] = useState(false);
  const [isUtilityDetailsVisible, setIsUtilityDetailsVisible] = useState(false);
  const [isCacDetailsVisible, setIsCacDetailsVisible] = useState(false);

  // Additional Upload State
  const [isSubmitCacModalVisible, setIsSubmitCacModalVisible] = useState(false);
  const [submitCacLoading, setSubmitCacLoading] = useState(false);
  const [finalSubmitCacLoading, setFinalSubmitCacLoading] = useState(false);
  const [isCategoryModalVisible, setIsCategoryModalVisible] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryForm] = Form.useForm();
  const [cacForm] = Form.useForm();
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressForm] = Form.useForm();

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      showLoader();
      const res = await httpGet(`${customBaseUrl.authUrl}/admin/users/${id}`);
      if (res?.success) {
        setUserDetails(res.data);
      } else {
        NotificationManager.error(res?.message || "Failed to fetch user.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      hideLoader();
      setLoading(false);
    }
  }, [id]);

  const fetchReferees = useCallback(async (page = 0) => {
    if (!id) return;
    try {
      setRefereesLoading(true);
      const response = await httpGet(`${customBaseUrl.referralUrl}/api/v1/admin/referrers/${id}/referees?pageNo=${page}&pageSize=${refereePagination.pageSize}`);
      if (response && response.data) {
        const refereeData = response.data.referees || response.data;
        if (refereeData.content) {
          setReferees(refereeData.content);
          setRefereePagination(prev => ({ ...prev, current: page, total: refereeData.totalElements || 0 }));
        } else if (response.data.profile) {
          setReferees(response.data.profile);
          setRefereePagination(prev => ({ ...prev, current: page, total: response.data.totalItems || response.data.totalElements || 0 }));
        } else if (Array.isArray(response.data)) {
          setReferees(response.data);
          setRefereePagination(prev => ({ ...prev, current: page, total: response.data[0]?.totalElements || response.data.length || 0 }));
        } else {
          setReferees([]);
          setRefereePagination(prev => ({ ...prev, current: page, total: 0 }));
        }
      } else {
        setReferees([]);
        setRefereePagination(prev => ({ ...prev, current: page, total: 0 }));
      }
    } catch (error) {
      console.error("Error fetching referees:", error);
      setReferees([]);
    } finally {
      setRefereesLoading(false);
    }
  }, [id, refereePagination.pageSize]);

  useEffect(() => {
    if (activeTab === "referees") {
      fetchReferees(0);
    }
  }, [activeTab, fetchReferees]);

  const fetchKycDetails = useCallback(async () => {
    setKycLoading(true);
    try {
      const [ninRes, cacRes, utilityRes] = await Promise.all([
        httpGet(`${customBaseUrl.authUrl}/nin-verification/user/${id}`),
        httpGet(`${customBaseUrl.authUrl}/api/v1/cac-business-accounts/user/${id}`),
        httpGet(`${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/user/${id}`)
      ]);
      console.log("KYC Data Fetched:", { nin: ninRes?.data, cac: cacRes?.data, utility: utilityRes?.data });
      setKycData({
        nin: (ninRes?.data && ninRes.data.length > 0) ? ninRes.data[0] : null,
        cac: (cacRes?.data && cacRes.data.length > 0) ? cacRes.data[0] : null,
        utility: (utilityRes?.data && utilityRes.data.length > 0) ? utilityRes.data[0] : null
      });
    } catch (err) {
      console.error("Error fetching KYC data", err);
    } finally {
      setKycLoading(false);
    }
  }, [id, userDetails?.email]);

  const uploadFile = async (file, type) => {
    if (!file) return null;
    const fileData = new FormData();
    fileData.append("file", file);
    try {
      const res = await httpPost(
        `${customBaseUrl.authUrl}/files/upload?documentType=${type}&username=${userDetails?.email || 'admin'}&folderName=kyc`,
        fileData,
        true
      );
      return res?.success ? res.data.fileUrl : null;
    } catch (err) {
      console.error(`Upload error for ${type}:`, err);
      return null;
    }
  };
  const formatFileList = (url, name = "Document") => {
    if (!url) return [];
    // If it's an object (old format), extract fileUrl
    const actualUrl = typeof url === 'object' ? url.fileUrl : url;
    if (!actualUrl) return [];
    return [{
      uid: Math.random().toString(),
      name: name,
      status: 'done',
      url: actualUrl,
      originFileObj: null
    }];
  };

  const openCacModal = () => {
    if (kycData.cac) {
      const cac = kycData.cac;
      console.log("Prefilling CAC Modal with data:", cac);
      console.log("CAC Data Keys:", Object.keys(cac));

      // Map flat keys back to directorsList
      const directors = [];
      for (let i = 1; i <= 10; i++) {
        // Support multiple naming conventions: director1Name, director1_name, director1name
        const dName = cac[`director${i}Name`] || cac[`director${i}_name`] || cac[`director${i}name`] || (cac.theDirectors && cac.theDirectors[i - 1]);
        const dBvn = cac[`director${i}Bvn`] || cac[`director${i}_bvn`] || cac[`director${i}bvn`];

        if (dName || dBvn) {
          directors.push({
            name: dName,
            bvn: dBvn,
            nin: cac[`director${i}Nin`] || cac[`director${i}_nin`],
            dob: cac[`director${i}Dob`] || cac[`director${i}_dob`],
            email: cac[`director${i}Email`] || cac[`director${i}_email`],
            phoneNumber: cac[`director${i}PhoneNumber`] || cac[`director${i}_phone_number`],
            streetAddress: cac[`director${i}StreetAddress`] || cac[`director${i}_street_address`],
            city: cac[`director${i}City`] || cac[`director${i}_city`],
            localGovernment: cac[`director${i}LocalGovernment`] || cac[`director${i}_local_government`],
            state: cac[`director${i}State`] || cac[`director${i}_state`],
            country: cac[`director${i}Country`] || cac[`director${i}_country`],
            photograph: formatFileList(cac[`director${i}PhotographUrl`] || cac[`director${i}_photograph_url`], "Photograph"),
            ninDoc: formatFileList(cac[`director${i}NinUrl`] || cac[`director${i}_nin_url`], "NIN Document"),
            utilityBill: formatFileList(cac[`director${i}UtilityBillUrl`] || cac[`director${i}_utility_bill_url`], "Utility Bill"),
          });
        }
      }

      // Map flat keys back to shareholdersList
      const shareholders = [];
      for (let i = 1; i <= 10; i++) {
        const sName = cac[`shareholder${i}Name`] || cac[`shareholder${i}_name`] || cac[`shareholder${i}name`];
        const sBvn = cac[`shareholder${i}Bvn`] || cac[`shareholder${i}_bvn`] || cac[`shareholder${i}bvn`];

        if (sName || sBvn) {
          shareholders.push({
            name: sName,
            bvn: sBvn,
            nin: cac[`shareholder${i}Nin`] || cac[`shareholder${i}_nin`],
            dob: cac[`shareholder${i}Dob`] || cac[`shareholder${i}_dob`],
            email: cac[`shareholder${i}Email`] || cac[`shareholder${i}_email`],
            phoneNumber: cac[`shareholder${i}PhoneNumber`] || cac[`shareholder${i}_phone_number`],
            streetAddress: cac[`shareholder${i}StreetAddress`] || cac[`shareholder${i}_street_address`],
            city: cac[`shareholder${i}City`] || cac[`shareholder${i}_city`],
            localGovernment: cac[`shareholder${i}LocalGovernment`] || cac[`shareholder${i}_local_government`],
            state: cac[`shareholder${i}State`] || cac[`shareholder${i}_state`],
            country: cac[`shareholder${i}Country`] || cac[`shareholder${i}_country`],
            photograph: formatFileList(cac[`shareholder${i}PhotographUrl`] || cac[`shareholder${i}_photograph_url`], "Photograph"),
            ninDoc: formatFileList(cac[`shareholder${i}NinUrl`] || cac[`shareholder${i}_nin_url`], "NIN Document"),
            utilityBill: formatFileList(cac[`shareholder${i}UtilityBillUrl`] || cac[`shareholder${i}_utility_bill_url`], "Utility Bill"),
          });
        }
      }

      cacForm.setFieldsValue({
        ...cac,
        cacNumber: cac.rcNumber || cac.cacNumber,
        directorsList: directors.length > 0 ? directors : [{}],
        shareholdersList: shareholders.length > 0 ? shareholders : [{}],
        cacCertificate: formatFileList(cac.cacCertificateUrl || cac.cac_certificate_url, "CAC Certificate"),
        memart: formatFileList(cac.memartUrl || cac.memart_url, "MEMART"),
        businessUtilityBill: formatFileList(cac.businessUtilityBillUrl || cac.business_utility_bill_url, "Business Utility Bill"),
        cacStatusReport: formatFileList(cac.cacStatusReportUrl || cac.cac_status_report_url, "CAC Status Report"),
      });
    } else {
      cacForm.resetFields();
      cacForm.setFieldsValue({
        directorsList: [{}],
        shareholdersList: [{}]
      });
    }
    setIsSubmitCacModalVisible(true);
  };

  const handleSubmitCac = async (values) => {
    setSubmitCacLoading(true);
    showLoader();
    try {
      // Get all values from the form to ensure we don't miss anything from inactive tabs
      const formValues = cacForm.getFieldsValue(true);

      const uploadedUrls = {};
      const flatDocTypes = {
        cacCertificate: 'CAC_CERTIFICATE',
        memart: 'MEMART',
        businessUtilityBill: 'BUSINESS_UTILITY_BILL',
        cacStatusReport: 'CAC_STATUS_REPORT'
      };

      for (const [field, type] of Object.entries(flatDocTypes)) {
        const fileList = formValues[field];
        if (fileList && fileList.length > 0) {
          if (fileList[0].originFileObj) {
            const url = await uploadFile(fileList[0].originFileObj, type);
            if (url) uploadedUrls[`${field}Url`] = url;
          } else if (fileList[0].url) {
            uploadedUrls[`${field}Url`] = fileList[0].url;
          }
        }
      }

      // Handle Dynamic Directors
      const directorsData = formValues.directorsList || [];
      for (let i = 0; i < directorsData.length; i++) {
        const d = directorsData[i];
        const idx = i + 1;
        if (d.photograph && d.photograph.length > 0) {
          if (d.photograph[0].originFileObj) {
            const url = await uploadFile(d.photograph[0].originFileObj, 'DIRECTOR_PHOTOGRAPH');
            if (url) uploadedUrls[`director${idx}PhotographUrl`] = url;
          } else if (d.photograph[0].url) {
            uploadedUrls[`director${idx}PhotographUrl`] = d.photograph[0].url;
          }
        }
        if (d.ninDoc && d.ninDoc.length > 0) {
          if (d.ninDoc[0].originFileObj) {
            const url = await uploadFile(d.ninDoc[0].originFileObj, 'DIRECTOR_NIN');
            if (url) uploadedUrls[`director${idx}NinUrl`] = url;
          } else if (d.ninDoc[0].url) {
            uploadedUrls[`director${idx}NinUrl`] = d.ninDoc[0].url;
          }
        }
        if (d.utilityBill && d.utilityBill.length > 0) {
          if (d.utilityBill[0].originFileObj) {
            const url = await uploadFile(d.utilityBill[0].originFileObj, 'DIRECTOR_UTILITY_BILL');
            if (url) uploadedUrls[`director${idx}UtilityBillUrl`] = url;
          } else if (d.utilityBill[0].url) {
            uploadedUrls[`director${idx}UtilityBillUrl`] = d.utilityBill[0].url;
          }
        }
      }

      // Handle Dynamic Shareholders
      const shareholdersData = formValues.shareholdersList || [];
      for (let i = 0; i < shareholdersData.length; i++) {
        const s = shareholdersData[i];
        const idx = i + 1;
        if (s.photograph && s.photograph.length > 0) {
          if (s.photograph[0].originFileObj) {
            const url = await uploadFile(s.photograph[0].originFileObj, 'SHAREHOLDER_PHOTOGRAPH');
            if (url) uploadedUrls[`shareholder${idx}PhotographUrl`] = url;
          } else if (s.photograph[0].url) {
            uploadedUrls[`shareholder${idx}PhotographUrl`] = s.photograph[0].url;
          }
        }
        if (s.ninDoc && s.ninDoc.length > 0) {
          if (s.ninDoc[0].originFileObj) {
            const url = await uploadFile(s.ninDoc[0].originFileObj, 'SHAREHOLDER_NIN');
            if (url) uploadedUrls[`shareholder${idx}NinUrl`] = url;
          } else if (s.ninDoc[0].url) {
            uploadedUrls[`shareholder${idx}NinUrl`] = s.ninDoc[0].url;
          }
        }
        if (s.utilityBill && s.utilityBill.length > 0) {
          if (s.utilityBill[0].originFileObj) {
            const url = await uploadFile(s.utilityBill[0].originFileObj, 'SHAREHOLDER_UTILITY_BILL');
            if (url) uploadedUrls[`shareholder${idx}UtilityBillUrl`] = url;
          } else if (s.utilityBill[0].url) {
            uploadedUrls[`shareholder${idx}UtilityBillUrl`] = s.utilityBill[0].url;
          }
        }
      }

      const finalPayload = {
        name: formValues.businessName,
        phoneNo: formValues.phoneNo,
        postalAddress: formValues.postalAddress,
        businessPhoneNo: formValues.businessPhoneNo || "",
        taxIDNo: formValues.taxIDNo || "",
        businessName: formValues.businessName,
        tradeName: formValues.tradeName || "",
        industrialSector: formValues.industrialSector || "",
        email: formValues.email,
        address: formValues.address,
        companyRegDate: formValues.companyRegDate || "",
        contactPersonName: formValues.contactPersonName || "",
        businessType: formValues.businessType,
        businessNature: formValues.businessNature || "",
        webAddress: formValues.webAddress || "",
        dateIncorporated: formValues.dateIncorporated || "",
        businessCommencementDate: formValues.businessCommencementDate || "",
        registrationNumber: formValues.cacNumber,
        cacNumber: formValues.cacNumber,
        rcNumber: formValues.cacNumber,
        userId: parseInt(id),
        theDirectors: directorsData.map(d => d.name).filter(Boolean),
        reference1: formValues.reference1 || "",
        reference2: formValues.reference2 || "",
        referral: formValues.referral || "",
        primaryAccount: kycData.cac ? kycData.cac.primaryAccount : true,
        businessEmail: formValues.businessEmail || "",
        ...uploadedUrls
      };

      // Map dynamic member details
      for (let i = 0; i < directorsData.length; i++) {
        const d = directorsData[i];
        const idx = i + 1;
        finalPayload[`director${idx}Name`] = d.name || "";
        finalPayload[`director${idx}Bvn`] = d.bvn || "";
        finalPayload[`director${idx}Nin`] = d.nin || "";
        finalPayload[`director${idx}Dob`] = d.dob || "";
        finalPayload[`director${idx}Email`] = d.email || "";
        finalPayload[`director${idx}PhoneNumber`] = d.phoneNumber || "";
        finalPayload[`director${idx}StreetAddress`] = d.streetAddress || "";
        finalPayload[`director${idx}City`] = d.city || "";
        finalPayload[`director${idx}LocalGovernment`] = d.localGovernment || "";
        finalPayload[`director${idx}State`] = d.state || "";
        finalPayload[`director${idx}Country`] = d.country || "";
      }

      for (let i = 0; i < shareholdersData.length; i++) {
        const s = shareholdersData[i];
        const idx = i + 1;
        finalPayload[`shareholder${idx}Name`] = s.name || "";
        finalPayload[`shareholder${idx}Bvn`] = s.bvn || "";
        finalPayload[`shareholder${idx}Nin`] = s.nin || "";
        finalPayload[`shareholder${idx}Dob`] = s.dob || "";
        finalPayload[`shareholder${idx}Email`] = s.email || "";
        finalPayload[`shareholder${idx}PhoneNumber`] = s.phoneNumber || "";
        finalPayload[`shareholder${idx}StreetAddress`] = s.streetAddress || "";
        finalPayload[`shareholder${idx}City`] = s.city || "";
        finalPayload[`shareholder${idx}LocalGovernment`] = s.localGovernment || "";
        finalPayload[`shareholder${idx}State`] = s.state || "";
        finalPayload[`shareholder${idx}Country`] = s.country || "";
      }

      const endpoint = kycData.cac
        ? `${customBaseUrl.authUrl}/api/v1/cac-business-accounts/${kycData.cac.id}`
        : `${customBaseUrl.authUrl}/api/v1/cac-business-accounts`;

      const res = kycData.cac
        ? await httpPut(endpoint, finalPayload)
        : await httpPost(endpoint, finalPayload);
      if (res?.success) {
        NotificationManager.success("Business registered successfully!");
        setIsSubmitCacModalVisible(false);
        fetchKycDetails();
      } else {
        const errorMsg = res?.error?.message || res?.message || "Submission failed";
        NotificationManager.error(errorMsg);
      }
    } catch (err) {
      console.error("CAC Submission Error:", err);
      NotificationManager.error("An error occurred during submission");
    } finally {
      setSubmitCacLoading(false);
      hideLoader();
    }
  };

  const handleFinalSubmitCac = async (accountId) => {
    try {
      setFinalSubmitCacLoading(true);
      showLoader();
      const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/cac-business-accounts/${accountId}/submit`);
      if (res?.success) {
        NotificationManager.success("Business registration submitted successfully!");
        setIsCacDetailsVisible(false);
        fetchKycDetails();
      } else {
        const errorMsg = res?.error?.message || res?.message || "Submission failed";
        NotificationManager.error(errorMsg);
      }
    } catch (err) {
      console.error("Final CAC Submission Error:", err);
      NotificationManager.error("An error occurred during final submission");
    } finally {
      setFinalSubmitCacLoading(false);
      hideLoader();
    }
  };

  const fetchBillsHistory = useCallback(async (page = 0, size = 10) => {
    setBillsPaymentLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/transactions/user/${id}?transactionType=BILLS_PAYMENT&page=${page}&size=${size}`);
      if (res?.success) {
        setBillsPayment(res.data.content || []);
        setBillsPaymentPagination(prev => ({
          ...prev,
          total: res.data.totalElements,
          current: res.data.number
        }));
      }
    } catch (err) {
      console.error("Error fetching bills history", err);
    } finally {
      setBillsPaymentLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    if (activeTab === "kyc") {
      fetchKycDetails();
    } else if (activeTab === "bills") {
      fetchBillsHistory();
    }
  }, [activeTab, fetchKycDetails, fetchBillsHistory]);

  const handleActivationSubmit = async (values) => {
    const { reason } = values;
    setActivationLoading(true);
    showLoader();
    try {
      const response = await httpPut(
        `${customBaseUrl.authUrl}/admin/users/${id}/active?isActive=${pendingActivationState}&reason=${reason}`,
        {}
      );
      if (response.success || response.status) {
        NotificationManager.success(`User ${pendingActivationState ? "activated" : "deactivated"} successfully!`);
        setIsActivationModalVisible(false);
        activationForm.resetFields();
        fetchUserDetails();
      } else {
        NotificationManager.error(response?.message || "Operation failed");
      }
    } catch (err) {
      NotificationManager.error("An error occurred");
    } finally {
      hideLoader();
      setActivationLoading(false);
    }
  };

  const handleActivation = (isActive) => {
    setPendingActivationState(isActive);
    setIsActivationModalVisible(true);
  };

  const handleUpdateCategory = async (values) => {
    const { category, remarks } = values;
    setCategoryLoading(true);
    showLoader();
    try {
      const response = await httpPut(
        `${customBaseUrl.authUrl}/admin/users/${id}/category?category=${category}&remarks=${remarks}`,
        {}
      );
      if (response.success || response.status) {
        NotificationManager.success(response.message || "User category update request submitted!");
        setIsCategoryModalVisible(false);
        categoryForm.resetFields();
        fetchUserDetails();
      } else {
        NotificationManager.error(response?.message || "Operation failed");
      }
    } catch (err) {
      NotificationManager.error("An error occurred");
    } finally {
      hideLoader();
      setCategoryLoading(false);
    }
  };

  const handleUpdateAddress = async (values) => {
    setAddressLoading(true);
    showLoader();
    try {
      const payload = {
        userId: Number(id),
        streetAddress: values.streetAddress,
        city: values.city,
        state: values.state,
        country: values.country,
        reason: values.reason,
      };
      const response = await httpPost(
        `${customBaseUrl.authUrl}/api/v1/admin/user-address-changes`,
        payload
      );
      if (response.success || response.status) {
        NotificationManager.success("Address change request submitted successfully!");
        setIsAddressModalVisible(false);
        addressForm.resetFields();
        fetchUserDetails();
      } else {
        NotificationManager.error(response?.message || "Operation failed");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred");
    } finally {
      hideLoader();
      setAddressLoading(false);
    }
  };

  // Utility Bill Upload Flow
  const handleSubmitUtilityBill = async (values) => {
    setSubmitUtilityBillLoading(true);
    showLoader();
    try {
      let fileUrl = '';
      if (values.file && values.file.length > 0) {
        fileUrl = await uploadFile(values.file[0].originFileObj, 'UTILITY_BILL');
      }

      const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/utility-bill-verifications/submit`, {
        userId: parseInt(id),
        utilityBillUrl: fileUrl,
        utilityBillType: values.utilityBillType,
        utilityProvider: values.utilityProvider || '',
        accountNumber: userDetails?.primaryAccountNumber || '',
        remarks: values.remarks || ''
      });
      if (res?.success) {
        NotificationManager.success("Utility Bill submitted successfully");
        setIsSubmitUtilityBillModalVisible(false);
        utilityForm.resetFields();
        fetchKycDetails();
      } else {
        NotificationManager.error(res?.message || "Submission failed");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred");
    } finally {
      setSubmitUtilityBillLoading(false);
      hideLoader();
    }
  };

  const handleSubmitNin = async (values) => {
    setSubmitNinLoading(true);
    showLoader();
    try {
      let imageUrl = '';
      if (values.file && values.file.length > 0) {
        imageUrl = await uploadFile(values.file[0].originFileObj, 'NIN_IMAGE');
      }

      const res = await httpPost(`${customBaseUrl.authUrl}/auth-serviceV3/nin-verification`, {
        userId: parseInt(id),
        nin: values.nin,
        imageUrl: imageUrl,
        status: "PENDING"
      });
      if (res?.success) {
        NotificationManager.success("NIN submitted successfully");
        setIsSubmitNinModalVisible(false);
        ninForm.resetFields();
        fetchKycDetails();
      } else {
        NotificationManager.error(res?.message || "Submission failed");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred");
    } finally {
      setSubmitNinLoading(false);
      hideLoader();
    }
  };

  const convertToCSV = (data) => {
    const headers = [
      "Serial Number", "Transaction Date", "Transaction ID", "Reference",
      "Type", "Nature", "Amount", "Fee", "Narration",
      "From Account", "To Account", "Payer Name", "Receiver Name", "Status", "Balance"
    ];

    const rows = (data.transactions || []).map(tx => [
      tx.serialNumber || "",
      tx.transactionDate || "",
      tx.transactionId || "",
      tx.reference || "",
      tx.transactionType || "",
      tx.transactionNature || "",
      tx.amount || 0,
      tx.fee !== null && tx.fee !== undefined ? tx.fee : "",
      tx.narration ? `"${tx.narration.replace(/"/g, '""')}"` : "",
      tx.fromAccount || "",
      tx.toAccount || "",
      tx.payerName ? `"${tx.payerName.replace(/"/g, '""')}"` : "",
      tx.receiverName ? `"${tx.receiverName.replace(/"/g, '""')}"` : "",
      tx.status || "",
      tx.balance !== null && tx.balance !== undefined ? tx.balance : ""
    ]);

    const summary = [
      ["Account Number", data.accountNumber || ""],
      ["Statement Period", data.statementPeriod?.formattedPeriod || ""],
      ["Opening Balance", data.openingBalance || 0],
      ["Closing Balance", data.closingBalance || 0],
      ["Total Debits", data.totalDebits || 0],
      ["Total Credits", data.totalCredits || 0],
      ["Transaction Count", data.transactionCount || 0],
      [],
      headers
    ];

    return [...summary, ...rows].map(e => e.join(",")).join("\n");
  };

  const base64ToBlob = (base64, type = 'application/pdf') => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: type });
  };

  const generatePDFStatement = (data) => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // WayaBank Header
    doc.setFontSize(22);
    doc.setTextColor(255, 103, 0); // #ff6700
    doc.text("WayaBank", 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Official Account Statement", 14, 25);

    // Account Summary Info
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Account Number: ${data.accountNumber || ""}`, 14, 35);
    doc.text(`Statement Period: ${data.statementPeriod?.formattedPeriod || ""}`, 14, 40);

    const summaryData = [
      ["Opening Balance", `NGN ${data.openingBalance || 0}`],
      ["Closing Balance", `NGN ${data.closingBalance || 0}`],
      ["Total Debits", `NGN ${data.totalDebits || 0}`],
      ["Total Credits", `NGN ${data.totalCredits || 0}`],
      ["Transaction Count", `${data.transactionCount || 0}`]
    ];

    doc.autoTable({
      startY: 45,
      head: [["Summary Metric", "Value"]],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [255, 103, 0] },
      margin: { left: 14, right: 14 }
    });

    // Transactions Table
    const tableColumn = ["S/N", "Date", "Transaction ID", "Reference", "Type", "Nature", "Amount", "Fee", "Status"];
    const tableRows = (data.transactions || []).map(tx => [
      tx.serialNumber || "",
      tx.transactionDate ? moment(tx.transactionDate).format('YYYY-MM-DD HH:mm:ss') : "",
      tx.transactionId || "",
      tx.reference || "",
      tx.transactionType || "",
      tx.transactionNature || "",
      tx.amount || 0,
      tx.fee !== null && tx.fee !== undefined ? tx.fee : 0,
      tx.status || ""
    ]);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [0, 21, 41] }, // Dark header
      styles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });

    doc.save(`Statement_${data.accountNumber || "Account"}_${moment().format('YYYYMMDD')}.pdf`);
  };

  const handleGenerateStatement = async (values) => {
    const { dateRange, accountNumber, isPdf } = values;
    if (!dateRange || !accountNumber) {
      NotificationManager.error("Please fill all fields");
      return;
    }
    setStatementLoading(true);
    const [fromDate, toDate] = dateRange;
    const params = new URLSearchParams({
      fromDate: fromDate.format("YYYY-MM-DD"),
      toDate: toDate.format("YYYY-MM-DD"),
      isPdf: isPdf || false
    });

    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/account/${accountNumber}/statement?${params.toString()}`);
      const responseData = res?.data || res;
      if (responseData) {
        if (isPdf) {
          if (responseData.pdfContent) {
            const blob = base64ToBlob(responseData.pdfContent, 'application/pdf');
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Statement_${accountNumber}_${moment().format('YYYYMMDD')}.pdf`;
            link.click();
          } else if (responseData.transactions) {
            // Fallback to client-side generation if pdfContent is missing but transaction data is present
            generatePDFStatement(responseData);
          } else {
            NotificationManager.error("No statement content found in response");
          }
        } else {
          if (responseData.transactions) {
            const csvContent = convertToCSV(responseData);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Statement_${accountNumber}_${moment().format('YYYYMMDD')}.csv`;
            link.click();
          } else {
            NotificationManager.error("No transaction data found in statement");
          }
        }
      } else {
        NotificationManager.error("No statement data found");
      }
      setIsStatementModalVisible(false);
    } catch (err) {
      console.error(err);
      NotificationManager.error("Failed to generate statement");
    } finally {
      setStatementLoading(false);
    }
  };

  const handleApproveCac = async (cacId) => {
    const { value: approver } = await Swal.fire({
      title: "Approve Business Registration",
      input: "text",
      inputLabel: "Approver Name",
      showCancelButton: true,
      confirmButtonColor: '#ff6700'
    });
    if (approver) {
      showLoader();
      const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/cac-business-accounts/${cacId}/approve?approvedBy=${approver}`, {});
      hideLoader();
      if (res?.success) {
        NotificationManager.success("Business approved successfully");
        fetchKycDetails();
      } else {
        NotificationManager.error(res?.message || "Failed to approve");
      }
    }
  };

  const handleRestoreKycTier = async () => {
    const result = await Swal.fire({
      title: "Restore KYC Tier?",
      text: "Are you sure you want to force restore the KYC tier for this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6700",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, restore it!",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      showLoader();
      setRestoreLoading(true);
      try {
        const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/admin/users/${id}/tier-restoration/force`, {});
        if (res?.success || res?.status) {
          NotificationManager.success(res?.message || "KYC tier restored successfully!");
          fetchUserDetails();
          fetchKycDetails();
        } else {
          NotificationManager.error(res?.message || "Failed to restore KYC tier");
        }
      } catch (err) {
        console.error(err);
        NotificationManager.error("An error occurred while restoring KYC tier");
      } finally {
        hideLoader();
        setRestoreLoading(false);
      }
    }
  };

  if (loading && !userDetails) return <Layout><div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div></Layout>;
  if (!userDetails) return <Layout><div style={{ padding: 40, textAlign: 'center' }}>User not found.</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: '0 24px' }}>
        {/* Navigation Bar */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            type="text"
            onClick={() => navigate('/all-users')}
            style={{ fontSize: 16 }}
          />
          <Title level={4} style={{ margin: 0 }}>Customer Profile</Title>
        </div>

        {/* Profile Header Summary */}
        <Card className="profile-header-card" style={{ marginBottom: 24, borderRadius: 16, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <Row gutter={24} align="middle">
            <Col>
              <Avatar
                size={100}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#ff6700' }}
                src={userDetails.profileImage}
              />
            </Col>
            <Col flex="auto">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Title level={3} style={{ margin: '0 0 4px 0', textTransform: 'capitalize' }}>
                    {userDetails.fullName || `${userDetails.firstName} ${userDetails.lastName}`}
                  </Title>
                  <Space split={<Divider type="vertical" />}>
                    <Text type="secondary">{userDetails.email}</Text>
                    <Text type="secondary">{userDetails.phoneNumber}</Text>
                    <Tag color={userDetails.isActive ? "success" : "error"} style={{ borderRadius: 12 }}>
                      {userDetails.isActive ? "ACTIVE" : "INACTIVE"}
                    </Tag>
                    <Tag color={userDetails.isFullyVerified ? "gold" : "default"}>
                      {userDetails.tierDisplayName || `TIER ${userDetails.tierLevel || 1}`}
                    </Tag>
                  </Space>
                </div>
                <Space>
                  <Button
                    onClick={() => setIsStatementModalVisible(true)}
                    icon={<FileProtectOutlined />}
                    style={{ borderRadius: 8 }}
                  >
                    Statement
                  </Button>
                  <Button
                    danger={userDetails.isActive}
                    type={userDetails.isActive ? "default" : "primary"}
                    onClick={() => handleActivation(!userDetails.isActive)}
                    icon={userDetails.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                    style={{ borderRadius: 8, cursor: "pointer" }}
                  >
                    {userDetails.isActive ? "Deactivate" : "Activate"}
                  </Button>
                </Space>
              </div>

              <Row gutter={48} style={{ marginTop: 24 }}>
                <Col>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase' }}>Main Account</Text>
                  <Title level={5} style={{ margin: 0 }}>{userDetails.primaryAccountNumber || 'N/A'}</Title>
                </Col>
                {/* <Col>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase' }}>Available Balance</Text>
                  <Title level={5} style={{ margin: 0, color: '#ff6700' }}>₦{parseFloat(userDetails.walletBalance || 0).toLocaleString()}</Title>
                </Col> */}
                <Col>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase' }}>Daily Limit</Text>
                  <Title level={5} style={{ margin: 0 }}>₦{parseFloat(userDetails.dailyTransactionLimit || 0).toLocaleString()}</Title>
                </Col>
                <Col>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase' }}>One-Time Limit</Text>
                  <Title level={5} style={{ margin: 0 }}>₦{parseFloat(userDetails.oneTimeTransferLimit || 0).toLocaleString()}</Title>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>

        {/* Tabbed Content Section */}
        <div className="profile-tabs-wrapper">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="premium-tabs"
            type="card"
          >
            <TabPane
              tab={<span><UserOutlined />Details</span>}
              key="details"
            >
              <Card style={{ borderRadius: '0 0 16px 16px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Descriptions title="Identity Information" bordered column={2}>
                  <Descriptions.Item label="First Name">{userDetails.firstName}</Descriptions.Item>
                  <Descriptions.Item label="Last Name">{userDetails.lastName}</Descriptions.Item>
                  <Descriptions.Item label="Gender">{userDetails.gender || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">{userDetails.dateOfBirth ? moment(userDetails.dateOfBirth).format('LL') : '—'}</Descriptions.Item>
                  <Descriptions.Item label="BVN">{userDetails.bvn || '—'}</Descriptions.Item>
                  <Descriptions.Item label="User Type">{userDetails.userType || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Customer Category">
                    <Space>
                      {userDetails.customerCategory || '—'}
                      {userDetails.userType !== 'PERSONAL' && (
                        <Button size="small" type="link" icon={<EditOutlined />} onClick={() => {
                          categoryForm.setFieldsValue({ category: userDetails.customerCategory });
                          setIsCategoryModalVisible(true);
                        }} />
                      )}
                    </Space>
                  </Descriptions.Item>
                  {userDetails.userType === 'CORPORATE' && (
                    <Descriptions.Item label="Merchant ID">{userDetails.merchantId || '—'}</Descriptions.Item>
                  )}
                </Descriptions>
                <Divider />
                <Descriptions title="Contact & Location" bordered column={2}>
                  <Descriptions.Item label="Email" span={2}>{userDetails.email}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{userDetails.phoneNumber}</Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>
                    <Space>
                      {userDetails.streetAddress || userDetails.address || '—'}
                      <Button size="small" type="link" icon={<EditOutlined />} onClick={() => {
                        addressForm.setFieldsValue({
                          streetAddress: userDetails.streetAddress || userDetails.address || '',
                          city: userDetails.city || '',
                          state: userDetails.state || '',
                          country: userDetails.country || '',
                          reason: ''
                        });
                        setIsAddressModalVisible(true);
                      }} />
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="City">{userDetails.city || '—'}</Descriptions.Item>
                  <Descriptions.Item label="State">{userDetails.state || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Country">{userDetails.country || '—'}</Descriptions.Item>
                </Descriptions>
              </Card>
            </TabPane>

            <TabPane
              tab={<span><WalletOutlined />Wallets</span>}
              key="accounts"
            >
              <CustomerAccounts id={id} />
            </TabPane>

            <TabPane
              tab={<span><HistoryOutlined />Transaction History</span>}
              key="transactions"
            >
              <Transactions id={id} activeTab={activeTab} />
            </TabPane>

            <TabPane
              tab={<span><FileSearchOutlined />Activity Logs</span>}
              key="activity-logs"
            >
              <ActivityLogs id={id} activeTab={activeTab} />
            </TabPane>

            <TabPane
              tab={<span><BellOutlined />OTP Logs</span>}
              key="otp"
            >
              <CustomerActivities id={id} identifier={userDetails.email || userDetails.phoneNumber} activeTab={activeTab} />
            </TabPane>

            <TabPane
              tab={<span><SolutionOutlined />KYC Verifications</span>}
              key="kyc"
            >
              <Card loading={kycLoading} style={{ borderRadius: '0 0 16px 16px' }}>
                <Row gutter={[24, 24]}>
                  <Col span={24}>
                    <Card
                      type="inner"
                      title="KYC Tier Management"
                      extra={
                        <Button
                          type="primary"
                          style={{ backgroundColor: '#ff6700', borderColor: '#ff6700', borderRadius: 8, color: '#fff' }}
                          onClick={handleRestoreKycTier}
                          loading={restoreLoading}
                        >
                          Restore KYC Tier
                        </Button>
                      }
                    >
                      <Descriptions column={2} size="small">
                        <Descriptions.Item label="Current Tier">
                          <Tag color="gold" style={{ borderRadius: 8 }}>
                            {userDetails?.tierDisplayName || `TIER ${userDetails?.tierLevel || 1}`}
                          </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Verification Status">
                          <Tag color={userDetails?.isFullyVerified ? "success" : "default"} style={{ borderRadius: 8 }}>
                            {userDetails?.isFullyVerified ? "FULLY VERIFIED" : "PARTIALLY VERIFIED"}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card
                      type="inner"
                      title="NIN Verification"
                      extra={
                        <Space>
                          {kycData.nin && (
                            <Button size="small" type="default" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setIsNinDetailsVisible(true); }}>
                              View
                            </Button>
                          )}
                          <Button size="small" type="primary" icon={<UploadOutlined />} onClick={(e) => { e.stopPropagation(); setIsSubmitNinModalVisible(true); }} style={{ color: '#fff' }}>
                            Upload
                          </Button>
                        </Space>
                      }
                    >
                      {kycData.nin ? (
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="NIN">{kycData.nin.nin}</Descriptions.Item>
                          <Descriptions.Item label="Name">{`${kycData.nin.firstname} ${kycData.nin.surname}`}</Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag color={kycData.nin.active ? "success" : "warning"}>
                              {kycData.nin.active ? "ACTIVE" : "INACTIVE"}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <Tag color="default">UNAVAILABLE</Tag>
                          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>No NIN data found</Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card
                      type="inner"
                      title="Utility Bill"
                      extra={
                        <Space>
                          {kycData.utility && (
                            <Button size="small" type="default" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setIsUtilityDetailsVisible(true); }}>
                              View
                            </Button>
                          )}
                          <Button size="small" type="primary" icon={<UploadOutlined />} onClick={(e) => { e.stopPropagation(); setIsSubmitUtilityBillModalVisible(true); }} style={{ color: '#fff' }}>
                            Upload
                          </Button>
                        </Space>
                      }
                    >
                      {kycData.utility ? (
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Type">{kycData.utility.utilityBillType || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Provider">{kycData.utility.utilityProvider || '—'}</Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag color={kycData.utility.status === 'APPROVED' ? "success" : "processing"}>
                              {kycData.utility.status || "PENDING"}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <Tag color="default">UNAVAILABLE</Tag>
                          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>No utility bill submitted</Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                  <Col span={24}>
                    <Card
                      type="inner"
                      title="Business (CAC) Registration"
                      extra={
                        <Space>
                          {kycData.cac && (
                            <Button size="small" type="default" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setIsCacDetailsVisible(true); }}>
                              View
                            </Button>
                          )}
                          {(!kycData.cac || kycData.cac.accountStatus === 'DRAFT') && (
                            <Button size="small" type="primary" icon={<UploadOutlined />} onClick={(e) => { e.stopPropagation(); openCacModal(); }} style={{ color: '#fff' }}>
                              {kycData.cac ? "Update Details" : "Upload"}
                            </Button>
                          )}
                          {(kycData.cac?.accountStatus === 'PENDING') && (
                            <Button type="primary" size="small" onClick={(e) => { e.stopPropagation(); handleApproveCac(kycData.cac.id); }}>Approve</Button>
                          )}
                        </Space>
                      }
                    >
                      {kycData.cac ? (
                        <Descriptions bordered column={2} size="small">
                          <Descriptions.Item label="Business Name">{kycData.cac.businessName}</Descriptions.Item>
                          <Descriptions.Item label="RC Number">{kycData.cac.cacNumber || kycData.cac.rcNumber}</Descriptions.Item>
                          <Descriptions.Item label="Business Type">{kycData.cac.businessType}</Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag color={kycData.cac.accountStatus === 'APPROVED' ? 'success' : 'warning'}>
                              {kycData.cac.accountStatus}
                            </Tag>
                          </Descriptions.Item>
                        </Descriptions>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '10px 0' }}>
                          <Tag color="default">UNAVAILABLE</Tag>
                          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>No business registration found</Text>
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Card>
            </TabPane>

            <TabPane
              tab={<span><UnlockOutlined />Permissions</span>}
              key="permissions"
            >
              <Card style={{ borderRadius: '0 0 16px 16px' }}>
                <Table
                  dataSource={userDetails.permissions || []}
                  columns={[
                    { title: 'Permission Name', dataIndex: 'name', key: 'name' },
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Status', key: 'status', render: (val, record) => <Tag color={record.isActive ? "green" : "red"}>{record.isActive ? "ENABLED" : "DISABLED"}</Tag> }
                  ]}
                  pagination={false}
                  size="small"
                />
              </Card>
            </TabPane>

            <TabPane
              tab={<span><DollarOutlined />Bills</span>}
              key="bills"
            >
              <Card style={{ borderRadius: '0 0 16px 16px' }}>
                <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={5} style={{ margin: 0 }}>Bills Payment History</Title>
                  <Button type="primary" onClick={() => setIsBillsPaymentModalVisible(true)} icon={<PlusOutlined />}>
                    Make a Bill Payment
                  </Button>
                </div>

                <Table
                  dataSource={billsPayment}
                  loading={billsPaymentLoading}
                  columns={[
                    { title: 'Transaction ID', dataIndex: 'transactionId', key: 'transactionId' },
                    { title: 'Bill Type', dataIndex: 'billType', key: 'billType' },
                    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `₦${parseFloat(val || 0).toLocaleString()}` },
                    { title: 'Status', dataIndex: 'status', key: 'status', render: (val) => <Tag color={val === 'SUCCESS' ? 'success' : 'warning'}>{val}</Tag> },
                    { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (val) => moment(val).format('LLL') }
                  ]}
                  pagination={{
                    current: billsPaymentPagination.current + 1,
                    pageSize: billsPaymentPagination.pageSize,
                    total: billsPaymentPagination.total,
                    onChange: (page) => fetchBillsHistory(page - 1)
                  }}
                  size="small"
                />
              </Card>
            </TabPane>

            <TabPane
              tab={<span><UserOutlined />Referees</span>}
              key="referees"
            >
              <Card style={{ borderRadius: '0 0 16px 16px' }}>
                <Table
                  dataSource={referees}
                  loading={refereesLoading}
                  rowKey={(record) => record.id || record.userId || record.phoneNumber}
                  columns={[
                    { title: 'Name', dataIndex: 'name', key: 'name', render: (text) => <Text strong>{text || 'N/A'}</Text> },
                    { title: 'Phone Number', dataIndex: 'phoneNumber', key: 'phoneNumber' },
                    { title: 'Ref Code', dataIndex: 'referenceCode', key: 'referenceCode', render: (text) => <Tag color="cyan">{text || '—'}</Tag> },
                    {
                      title: 'Category',
                      key: 'category',
                      render: (_, record) => (
                        <Space>
                          <Tag color={record.corporate ? 'blue' : 'purple'}>
                            {record.corporate ? 'Corporate' : 'Personal'}
                          </Tag>
                          {record.customerCategory && <Tag color="orange">{record.customerCategory}</Tag>}
                        </Space>
                      )
                    },
                    {
                      title: 'Reg. Date',
                      dataIndex: 'createdAt',
                      key: 'createdAt',
                      render: (date) => date ? moment(date).format('DD MMM YYYY') : '—'
                    }
                  ]}
                  pagination={{
                    current: refereePagination.current + 1,
                    pageSize: refereePagination.pageSize,
                    total: refereePagination.total,
                    onChange: (page) => fetchReferees(page - 1)
                  }}
                  size="small"
                />
              </Card>
            </TabPane>

            <TabPane
              tab={<span><GiftOutlined />Rewards</span>}
              key="rewards"
            >
              <CustomerRewards id={id} activeTab={activeTab} />
            </TabPane>
          </Tabs>
        </div>
      </div>

      {/* Activation/Deactivation Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ExclamationCircleOutlined style={{ color: pendingActivationState ? '#52c41a' : '#ff4d4f' }} />
            <span>Confirm User {pendingActivationState ? "Activation" : "Deactivation"}</span>
          </div>
        }
        open={isActivationModalVisible}
        onCancel={() => {
          setIsActivationModalVisible(false);
          activationForm.resetFields();
        }}
        footer={null}
        centered
        width={450}
        className="premium-modal"
      >
        <div style={{ padding: '10px 0' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
            {pendingActivationState
              ? "Activating this user will allow them to access their account and perform transactions."
              : "Deactivating this user will restrict their access to the platform and block all transactions."}
          </Text>
          <Form form={activationForm} layout="vertical" onFinish={handleActivationSubmit}>
            <Form.Item
              name="reason"
              label="Reason / Remarks"
              rules={[{ required: true, message: 'Please provide a reason' }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Enter detailed reason for this action..."
                prefix={<EditOutlined />}
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <Button
                style={{ flex: 1, borderRadius: 8 }}
                onClick={() => {
                  setIsActivationModalVisible(false);
                  activationForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                danger={!pendingActivationState}
                htmlType="submit"
                loading={activationLoading}
                style={{
                  flex: 1,
                  borderRadius: 8,
                  background: pendingActivationState ? '#52c41a' : '#ff4d4f',
                  borderColor: pendingActivationState ? '#52c41a' : '#ff4d4f'
                }}
              >
                Confirm {pendingActivationState ? "Activation" : "Deactivation"}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Account Statement Modal */}
      <Modal
        title="Generate Account Statement"
        open={isStatementModalVisible}
        onCancel={() => setIsStatementModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={handleGenerateStatement}>
          <Form.Item name="accountNumber" label="Select Wallet" rules={[{ required: true }]}>
            <Input placeholder="Enter Account Number" />
          </Form.Item>
          <Form.Item name="dateRange" label="Date Range" rules={[{ required: true }]}>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isPdf" valuePropName="checked">
            <Checkbox>Save as PDF</Checkbox>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={statementLoading} block>Generate</Button>
        </Form>
      </Modal>

      {/* Bill Payment Modal */}
      <Modal
        title="Admin Bill Payment"
        open={isBillsPaymentModalVisible}
        onCancel={() => setIsBillsPaymentModalVisible(false)}
        footer={null}
        width={800}
      >
        <AdminBillsPayment
          userId={id}
          userWallets={[]}
          onPaymentSuccess={() => {
            setIsBillsPaymentModalVisible(false);
            fetchBillsHistory();
          }}
        />
      </Modal>

      {/* NIN Submission Modal */}
      <Modal
        title="Submit NIN Verification"
        open={isSubmitNinModalVisible}
        onCancel={() => setIsSubmitNinModalVisible(false)}
        footer={null}
      >
        <Form form={ninForm} layout="vertical" onFinish={handleSubmitNin}>
          <Form.Item name="nin" label="NIN Number" rules={[{ required: true }]}>
            <Input placeholder="Enter 11-digit NIN" maxLength={11} />
          </Form.Item>
          <Form.Item name="file" label="NIN Document/Photo" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} rules={[{ required: true }]}>
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />} block>Select NIN Document</Button>
            </Upload>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitNinLoading} block style={{ marginTop: 16 }}>
            Submit NIN
          </Button>
        </Form>
      </Modal>

      {/* Utility Bill Submission Modal */}
      <Modal
        title="Submit Utility Bill"
        open={isSubmitUtilityBillModalVisible}
        onCancel={() => setIsSubmitUtilityBillModalVisible(false)}
        footer={null}
      >
        <Form form={utilityForm} layout="vertical" onFinish={handleSubmitUtilityBill}>
          <Form.Item name="utilityBillType" label="Bill Type" rules={[{ required: true }]}>
            <Select placeholder="Select utility type">
              <Option value="ELECTRICITY">Electricity Bill</Option>
              <Option value="WATER">Water Bill</Option>
              <Option value="WASTE">Waste Management</Option>
              <Option value="TENANCY">Tenancy Agreement</Option>
            </Select>
          </Form.Item>
          <Form.Item name="utilityProvider" label="Utility Provider" rules={[{ required: true, message: 'Utility provider is required' }]}>
            <Input placeholder="e.g. EKEDC, AEDC, Water Board" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={4} placeholder="Enter any remarks" />
          </Form.Item>
          <Form.Item name="file" label="Upload Utility Bill" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} rules={[{ required: true }]}>
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />} block>Select Bill Document</Button>
            </Upload>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitUtilityBillLoading} block style={{ marginTop: 16 }}>
            Submit Utility Bill
          </Button>
        </Form>
      </Modal>

      {/* NIN Verification Details Modal */}
      <Modal
        title="NIN Verification Details"
        open={isNinDetailsVisible}
        onCancel={() => setIsNinDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsNinDetailsVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
        className="premium-modal"
      >
        {kycData.nin && (
          <div style={{ padding: '10px 0' }}>
            <Row gutter={24}>
              <Col span={8}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <img
                    src={kycData.nin.imageUrl}
                    alt="NIN Holder"
                    style={{ width: '100%', borderRadius: 8, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                  />
                  <Tag color={kycData.nin.active ? "success" : "error"} style={{ marginTop: 10 }}>
                    {kycData.nin.active ? "VERIFIED" : "INACTIVE"}
                  </Tag>
                </div>
              </Col>
              <Col span={16}>
                <Descriptions title="Personal Information" column={1} bordered size="small">
                  <Descriptions.Item label="Full Name">{`${kycData.nin.firstname} ${kycData.nin.middlename || ""} ${kycData.nin.surname}`}</Descriptions.Item>
                  <Descriptions.Item label="NIN Number">{kycData.nin.nin}</Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">{kycData.nin.birthdate}</Descriptions.Item>
                  <Descriptions.Item label="Gender">{kycData.nin.gender?.toUpperCase()}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{kycData.nin.telephoneno}</Descriptions.Item>
                  <Descriptions.Item label="Address">{kycData.nin.residenceAddress}</Descriptions.Item>
                  <Descriptions.Item label="Verification Date">{moment(kycData.nin.verificationDate).format('LLL')}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Utility Bill Details Modal */}
      <Modal
        title="Utility Bill Details"
        open={isUtilityDetailsVisible}
        onCancel={() => setIsUtilityDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsUtilityDetailsVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
        className="premium-modal"
      >
        {kycData.utility && (
          <div style={{ padding: '10px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <img
                src={kycData.utility.utilityBillUrl}
                alt="Utility Bill"
                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
              />
            </div>
            <Descriptions title="Submission Details" column={2} bordered size="small">
              <Descriptions.Item label="Bill Type">{kycData.utility.utilityBillType}</Descriptions.Item>
              <Descriptions.Item label="Provider">{kycData.utility.utilityProvider}</Descriptions.Item>
              <Descriptions.Item label="Account Number">{kycData.utility.accountNumber || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={kycData.utility.status === 'APPROVED' ? "success" : "processing"}>
                  {kycData.utility.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>{kycData.utility.billingAddress}</Descriptions.Item>
              <Descriptions.Item label="Submitted On">{moment(kycData.utility.createdAt).format('LLL')}</Descriptions.Item>
              <Descriptions.Item label="Approved By">{kycData.utility.approvedBy || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Admin Remarks" span={2}>{kycData.utility.adminRemarks || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* CAC Details Modal */}
      <Modal
        title="Business Registration Details"
        open={isCacDetailsVisible}
        footer={[
          kycData.cac?.accountStatus === 'DRAFT' && (
            <Button
              key="submit"
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={finalSubmitCacLoading}
              onClick={() => handleFinalSubmitCac(kycData.cac.id)}
              style={{ background: '#52c41a', borderColor: '#52c41a' }}
            >
              Submit for Approval
            </Button>
          ),
          (kycData.cac?.accountStatus === 'DRAFT') && (
            <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => { setIsCacDetailsVisible(false); openCacModal(); }}>
              Edit Details
            </Button>
          ),
          <Button key="close" onClick={() => setIsCacDetailsVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
        className="premium-modal"
      >
        {kycData.cac && (
          <Descriptions bordered column={2}>
            {Object.entries(kycData.cac).map(([key, value]) => (
              <Descriptions.Item key={key} label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}>
                {typeof value === 'string' && value.startsWith('http') ? (
                  <a href={value} target="_blank" rel="noopener noreferrer">View Document</a>
                ) : (
                  value ? String(value) : 'N/A'
                )}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Modal>

      {/* CAC Upload Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <IdcardOutlined style={{ color: '#ff6700' }} />
            <span>Business Registration (CAC)</span>
          </div>
        }
        open={isSubmitCacModalVisible}
        onCancel={() => setIsSubmitCacModalVisible(false)}
        footer={null}
        width={1000}
        className="premium-modal"
        centered
      >
        <Form
          form={cacForm}
          layout="vertical"
          onFinish={handleSubmitCac}
          preserve={true}
          initialValues={{
            userId: id,
            businessName: userDetails?.businessName,
            email: userDetails?.email,
            name: `${userDetails?.firstName} ${userDetails?.lastName}`,
            directorsList: [{}],
            shareholdersList: [{}]
          }}
        >
          <Tabs defaultActiveKey="1" type="card" className="premium-tabs">
            <TabPane tab="Business Profile" key="1">
              <div style={{ padding: '20px 0' }}>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="businessName" label="Business Name" rules={[{ required: true }]}><Input placeholder="Legal Name" /></Form.Item></Col>
                  <Col span={8}><Form.Item name="tradeName" label="Trade Name"><Input placeholder="Trading As" /></Form.Item></Col>
                  <Col span={8}><Form.Item name="cacNumber" label="CAC / RC Number" rules={[{ required: true }]}><Input placeholder="RC123456" /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="registrationNumber" label="Reg Number"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="businessType" label="Business Type" rules={[{ required: true }]}><Select><Option value="LIMITED">Limited Company</Option><Option value="SOLE">Sole Proprietor</Option><Option value="PARTNER">Partnership</Option></Select></Form.Item></Col>
                  <Col span={8}><Form.Item name="industrialSector" label="Industrial Sector"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="companyRegDate" label="Company Reg Date"><Input type="date" /></Form.Item></Col>
                  <Col span={8}><Form.Item name="dateIncorporated" label="Date Incorporated"><Input type="date" /></Form.Item></Col>
                  <Col span={8}><Form.Item name="businessCommencementDate" label="Commencement Date"><Input type="date" /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="businessNature" label="Nature of Business"><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="taxIDNo" label="Tax ID / TIN"><Input /></Form.Item></Col>
                </Row>
                <Divider orientation="left" style={{ fontSize: 12, color: '#ff6700' }}>References & Referral</Divider>
                <Row gutter={16}>
                  <Col span={8}><Form.Item name="reference1" label="Reference 1"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="reference2" label="Reference 2"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="referral" label="Referral"><Input /></Form.Item></Col>
                </Row>
              </div>
            </TabPane>

            <TabPane tab="Contact & Address" key="2">
              <div style={{ padding: '20px 0' }}>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="email" label="Contact Email" rules={[{ required: true, type: 'email' }]}><Input /></Form.Item></Col>
                  <Col span={12}><Form.Item name="businessEmail" label="Business Email"><Input /></Form.Item></Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="phoneNo" label="Phone Number" rules={[{ required: true }]}><Input placeholder="+234..." /></Form.Item></Col>
                  <Col span={12}><Form.Item name="businessPhoneNo" label="Business Phone"><Input /></Form.Item></Col>
                </Row>
                <Form.Item name="address" label="Registered Address" rules={[{ required: true, message: 'Address is required' }]}><Input.TextArea rows={2} /></Form.Item>
                <Form.Item name="postalAddress" label="Postal Address" rules={[{ required: true, message: 'Postal address is required' }]}><Input.TextArea rows={2} /></Form.Item>
                <Row gutter={16}>
                  <Col span={12}><Form.Item name="webAddress" label="Website"><Input placeholder="https://..." /></Form.Item></Col>
                  <Col span={12}><Form.Item name="contactPersonName" label="Contact Person"><Input /></Form.Item></Col>
                </Row>
              </div>
            </TabPane>

            <TabPane tab="Directors" key="3">
              <div style={{ padding: '20px 0' }}>
                <Form.List name="directorsList">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div key={key} style={{ marginBottom: 24, border: '1px solid #f0f0f0', padding: 16, borderRadius: 8, position: 'relative' }}>
                          <Title level={5}>Director {index + 1}</Title>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              style={{ position: 'absolute', top: 16, right: 16 }}
                            />
                          )}
                          <Row gutter={16}>
                            <Col span={8}><Form.Item {...restField} name={[name, 'name']} label="Full Name"><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'bvn']} label="BVN"><Input maxLength={11} /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'nin']} label="NIN"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}><Form.Item {...restField} name={[name, 'dob']} label="DOB"><Input type="date" /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'email']} label="Email"><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'phoneNumber']} label="Phone"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}><Form.Item {...restField} name={[name, 'streetAddress']} label="Street Address"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={6}><Form.Item {...restField} name={[name, 'city']} label="City"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'localGovernment']} label="Local Government"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'state']} label="State"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'country']} label="Country"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'photograph']} label="Photograph" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1} listType="picture">
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Photo</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'ninDoc']} label="NIN Document" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Document</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'utilityBill']} label="Utility Bill" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Bill</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Another Director
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            <TabPane tab="Shareholders" key="4">
              <div style={{ padding: '20px 0' }}>
                <Form.List name="shareholdersList">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }, index) => (
                        <div key={key} style={{ marginBottom: 24, border: '1px solid #f0f0f0', padding: 16, borderRadius: 8, position: 'relative' }}>
                          <Title level={5}>Shareholder {index + 1}</Title>
                          {fields.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              style={{ position: 'absolute', top: 16, right: 16 }}
                            />
                          )}
                          <Row gutter={16}>
                            <Col span={8}><Form.Item {...restField} name={[name, 'name']} label="Full Name"><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'bvn']} label="BVN"><Input maxLength={11} /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'nin']} label="NIN"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}><Form.Item {...restField} name={[name, 'dob']} label="DOB"><Input type="date" /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'email']} label="Email"><Input /></Form.Item></Col>
                            <Col span={8}><Form.Item {...restField} name={[name, 'phoneNumber']} label="Phone"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={24}><Form.Item {...restField} name={[name, 'streetAddress']} label="Street Address"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={6}><Form.Item {...restField} name={[name, 'city']} label="City"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'localGovernment']} label="Local Government"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'state']} label="State"><Input /></Form.Item></Col>
                            <Col span={6}><Form.Item {...restField} name={[name, 'country']} label="Country"><Input /></Form.Item></Col>
                          </Row>
                          <Row gutter={16}>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'photograph']} label="Photograph" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1} listType="picture">
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Photo</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'ninDoc']} label="NIN Document" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Document</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item {...restField} name={[name, 'utilityBill']} label="Utility Bill" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                                <Upload beforeUpload={() => false} maxCount={1}>
                                  <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Bill</Button>
                                </Upload>
                              </Form.Item>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Another Shareholder
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </TabPane>

            <TabPane tab="Documents" key="5">
              <div style={{ padding: '20px 0' }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="cacCertificate" label="CAC Certificate" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                      <Upload beforeUpload={() => false} maxCount={1}>
                        <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Certificate</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="memart" label="MEMART" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                      <Upload beforeUpload={() => false} maxCount={1}>
                        <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select MEMART</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="businessUtilityBill" label="Business Utility Bill" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                      <Upload beforeUpload={() => false} maxCount={1}>
                        <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Utility Bill</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="cacStatusReport" label="CAC Status Report" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
                      <Upload beforeUpload={() => false} maxCount={1}>
                        <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Select Status Report</Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </TabPane>
          </Tabs>

          <Button type="primary" htmlType="submit" loading={submitCacLoading} block style={{ marginTop: 24, height: 45, fontSize: 16, background: '#ff6700', borderColor: '#ff6700' }}>
            {kycData.cac ? "Save" : "Save Draft"}
          </Button>
        </Form>
      </Modal>

      {/* Category Update Modal */}
      <Modal
        title="Update Customer Category"
        open={isCategoryModalVisible}
        onCancel={() => setIsCategoryModalVisible(false)}
        footer={null}
        className="premium-modal"
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleUpdateCategory}
          initialValues={{ category: userDetails?.customerCategory }}
        >
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select Category">
              <Option value="A">Category A</Option>
              <Option value="B">Category B</Option>
              <Option value="C">Category C</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="remarks"
            label="Remarks"
            rules={[{ required: true, message: 'Please provide remarks' }]}
          >
            <Input.TextArea rows={4} placeholder="Reason for category change..." />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={categoryLoading}
            block
            style={{ marginTop: 16, height: 40, background: '#ff6700', borderColor: '#ff6700' }}
          >
            Update Category
          </Button>
        </Form>
      </Modal>

      {/* Address Update Modal */}
      <Modal
        title="Propose Address Change"
        open={isAddressModalVisible}
        onCancel={() => setIsAddressModalVisible(false)}
        footer={null}
        className="premium-modal"
      >
        <Form
          form={addressForm}
          layout="vertical"
          onFinish={handleUpdateAddress}
        >
          <Form.Item
            name="streetAddress"
            label="Street Address"
            rules={[{ required: true, message: 'Please enter street address' }]}
          >
            <Input placeholder="123 Main St" />
          </Form.Item>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Please enter city' }]}
          >
            <Input placeholder="City" />
          </Form.Item>
          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: 'Please enter state' }]}
          >
            <Input placeholder="State" />
          </Form.Item>
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Please enter country' }]}
          >
            <Input placeholder="Country" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason for Change"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <Input.TextArea rows={4} placeholder="Reason for address change..." />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={addressLoading}
            block
            style={{ marginTop: 16, height: 40, background: '#ff6700', borderColor: '#ff6700' }}
          >
            Propose Address Change
          </Button>
        </Form>
      </Modal>

      <style>{`
        .premium-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        .premium-tabs .ant-tabs-tab {
          background: #f5f5f5 !important;
          border: none !important;
          margin-right: 4px !important;
          border-radius: 12px 12px 0 0 !important;
          padding: 12px 24px !important;
          transition: all 0.3s;
        }
        .premium-tabs .ant-tabs-tab-active {
          background: #ffffff !important;
        }
        .premium-tabs .ant-tabs-tab-active span {
          color: #ff6700 !important;
          font-weight: 600;
        }
        .premium-tabs .ant-tabs-tab:hover {
          color: #ff6700 !important;
        }
        .ant-descriptions-title {
          color: #ff6700 !important;
          font-size: 14px !important;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .premium-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }
        .premium-modal .ant-modal-header {
          border-bottom: 1px solid #f0f0f0;
          padding: 16px 24px;
        }
        .premium-modal .ant-modal-body {
          padding: 24px;
        }
        .ant-btn {
          cursor: pointer !important;
        }
        .ant-btn:disabled {
          cursor: not-allowed !important;
        }
      `}</style>
    </Layout>
  );
};

export default UserDetails;
